import React, { useState } from "react";
import CustomFieldModal from "./CustomFieldModal";
import useCustomField from "../../../../../hooks/useCustomField/UseCustomField";
import { MdDeleteOutline, MdOutlineEdit } from "react-icons/md";
import EditCustomFieldModal from "./EditCustomFieldModal";
import useUpdateCustomField from "../../../../../hooks/useCustomField/useUpdateCustomField";
import useEditCustomField from "../../../../../hooks/useCustomField/useEditCustomField";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import FieldEditModal from "./FieldEditModal/FieldEditModal";

const FilesAttachments = ({
  dynamicFields = [],
  hidden,
  onFieldChange, // Accept onFieldChange prop from parent
  require,
  Message,
}) => {
  const [showModal, setShowModal] = useState(false); // State for add field modal
  const [showDeleteModal, setShowDeleteModal] = useState(false); // State for delete confirmation modal
  const [fieldToDelete, setFieldToDelete] = useState(null); // State for the field to delete
  const [division, setDivision] = useState(""); // State for division
  const [message, setMessage] = useState({ type: "", text: "" }); // State for success/error messages

  const { submitCustomField } = useCustomField();
  const { updateCustomField } = useUpdateCustomField();
  const apiUrls = process.env.REACT_APP_API_URLS;

  const {
    showEditModal,
    selectedField,
    handleOpenEditModal,
    handleCloseEditModal,
    handleSubmitUpdateField,
  } = useEditCustomField();

  // Handle form submission for adding a new field
  const handleSubmit = async (data) => {
    const result = await submitCustomField(data);
    if (result.success) {
      toast.success("Field is Added Succefully", {
        position: "top-right",
        autoClose: 600,
      });

      setShowModal(false);
      setTimeout(() => {
        window.location.reload();
      }, 700);
    } else {
      toast.error(result.message, {
        position: "top-right",
        autoClose: 1000,
      });
      setShowModal(false);
    }
  };

  // Open Add Field Modal
  const handleOpenModal = () => {
    setDivision("Files Attachments"); // Set division value here
    setShowModal(true);
  };

  // Open Delete Confirmation Modal
  const handleDeleteField = (field) => {
    setFieldToDelete(field);
    setShowDeleteModal(true);
  };

  // Confirm deletion of the field
  const handleConfirmDelete = async () => {
    if (fieldToDelete) {
      try {
        const response = await axios.delete(
          `${apiUrls}/buyer_settings/custom-fields/${fieldToDelete.id}/`
        );

        if (response.status === 204 || response.status === 200) {
          setMessage({ type: "success", text: "Field deleted successfully!" });
          window.location.reload(); // Reload the page on success
        } else {
          setMessage({ type: "error", text: "Failed to delete the field." });
        }
      } catch (error) {
        setMessage({
          type: "error",
          text: "An error occurred while deleting the field.",
        });
      } finally {
        setShowDeleteModal(false);
        setFieldToDelete(null);
      }
    }
  };

  // Close Delete Confirmation Modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setFieldToDelete(null);
  };

  // Handle input changes and notify parent
  const handleInputChange = (e, field) => {
    const { value, files } = e.target;
    if (field.type === "file") {
      onFieldChange(field.column_name || field.name, files[0]); // Notify parent of file changes
    } else {
      onFieldChange(field.column_name || field.name, value); // Notify parent of other changes
    }
  };

  // Render input fields based on type
  const renderInputField = (field) => {
    switch (field.type) {
      case "file":
        return (
          <input
            type="file"
            id={field.column_name}
            className="mt-1 block w-full p-2 border-2 border-gray-200 shadow-lg rounded-md focus:outline-none focus:border-gray-300"
            onChange={(e) => handleInputChange(e, field)} // Notify parent of changes
            disabled={require}
            readOnly={require}
          />
        );
      case "text":
        return (
          <input
            type="text"
            id={field.column_name}
            name={field.column_name} // Use column_name as the field name
            placeholder={field.placeholder || ""}
            className="mt-1 block w-full p-2 border-2 border-gray-200 rounded-md focus:outline-none focus:border-gray-300"
            onChange={(e) => handleInputChange(e, field)} // Notify parent of changes
            disabled={require}
            readOnly={require}
          />
        );
      case "date":
        return (
          <input
            type="date"
            id={field.column_name}
            name={field.column_name} // Use column_name as the field name
            className="mt-1 block w-full p-2 border-2 border-gray-200 rounded-md focus:outline-none focus:border-gray-300"
            onChange={(e) => handleInputChange(e, field)} // Notify parent of changes
            disabled={require}
            readOnly={require}
          />
        );
      case "dropdown":
        const options = field.options ? field.options.split(",") : [];
        return (
          <select
            id={field.column_name}
            name={field.column_name} // Use column_name as the field name
            className="mt-1 block w-full p-2 border-2 border-gray-200 rounded-md focus:outline-none focus:border-gray-300"
            onChange={(e) => handleInputChange(e, field)} // Notify parent of changes
            disabled={require}
            readOnly={require}
          >
            <option value="" disabled>
              Select {field.field_label}
            </option>
            {options.map((option, index) => (
              <option key={index} value={option.trim()}>
                {option.trim()}
              </option>
            ))}
          </select>
        );
      case "radio":
        const radioOptions = field.options ? field.options.split(",") : [];
        return (
          <div className="flex gap-4">
            {radioOptions.map((option, index) => (
              <label key={index} className="flex items-center">
                <input
                  type="radio"
                  name={field.column_name}
                  value={option.trim()}
                  className="mr-2"
                  onChange={(e) => handleInputChange(e, field)} // Notify parent of changes
                  disabled={require}
                  readOnly={require}
                />
                {option.trim()}
              </label>
            ))}
          </div>
        );
      case "checkbox":
        const checkboxOptions = field.options ? field.options.split(",") : [];
        return (
          <div className="flex gap-4">
            {checkboxOptions.map((option, index) => (
              <label key={index} className="flex items-center">
                <input
                  type="checkbox"
                  name={field.column_name}
                  value={option.trim()}
                  className="mr-2"
                  onChange={(e) => handleInputChange(e, field)} // Notify parent of changes
                  disabled={require}
                  readOnly={require}
                />
                {option.trim()}
              </label>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

       const [isEditModalOpen, setEditModalOpen] = useState(false)
  const [formData, setFormData] = useState({
    label: '',
    placeholder: '',
    type: 'required',
  });

  const handleSave = () => {
    console.log("Form Data Saved:", formData);
    setEditModalOpen(false);
  };

  return (
    <>
      <div className="border-2 border-gray-200 rounded-md p-6 mt-8">
        <h2 className="text-lg font-semibold mb-4">
          <span className="underline">File Attachments :</span>
        </h2>

        {/* Display success/error messages */}
        {/* {message.text && (
          <div
            className={`p-2 mb-4 rounded-md ${
              message.type === "success"
                ? "bg-green-100 text-green-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {message.text}
          </div>
        )} */}

        {/* Static Fields */}
        <form className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between">
            <label htmlFor="companyLogo" className="block">
              Attach Company Logo<span className="text-red-500">*</span>
            </label>
             {Message === "dont show icons" ? (
                <></>
              ) : (<div className="flex items-center gap-5">
                <span onClick={() => {setFormData({label:"Attach Company Logo",placeholder:"selecy company logo"});;setEditModalOpen(true)}} className="text-gray-400 cursor-pointer">✎</span>
                <span className="text-gray-400 cursor-pointer scale-150">x</span>
              </div>)}
            </div>
            <input
              type="file"
              id="companyLogo"
              name="companyLogo" // Add a name for the field
              className="mt-1 block w-full p-2 outline-none rounded-md border-2 border-gray-200 shadow-sm focus:border-gray-300"
              onChange={(e) =>
                handleInputChange(e, { name: "companyLogo", type: "file" })
              } // Notify parent of changes
              disabled={require}
              readOnly={require}
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
            <label htmlFor="businessFiles" className="block">
              Attach other files relevant to your business promotion
              <span className="text-red-500">*</span>
            </label>
             {Message === "dont show icons" ? (
                <></>
              ) : (<div className="flex items-center gap-5">
                <span onClick={() => {setFormData({label:"Attach other files relevant to your business promotion",placeholder:"select other files relevant to your business promotion"});;setEditModalOpen(true)}} className="text-gray-400 cursor-pointer">✎</span>
                <span className="text-gray-400 cursor-pointer scale-150">x</span>
              </div>)}
            </div>
            <input
              type="file"
              id="businessFiles"
              name="businessFiles" // Add a name for the field
              className="mt-1 block w-full p-2 outline-none rounded-md border-2 border-gray-200 shadow-sm focus:border-gray-300"
              onChange={(e) =>
                handleInputChange(e, { name: "businessFiles", type: "file" })
              } // Notify parent of changes
              disabled={require}
              readOnly={require}
            />
          </div>

          {/* Dynamic Fields */}
          {dynamicFields
            .filter((field) => field) // Filter out undefined or null fields
            .map(
              (field) =>
                field.division == "Files Attachments" && (
                  <div key={field.column_name}>
                    <div className="flex justify-between">
                      <label className="block">
                        {field.field_label?.replace(/_/g, " ") ||
                          "Unnamed Field"}
                        {field.is_required && (
                          <span className="text-red-500">*</span>
                        )}
                      </label>
                      <div
                        className={`gap-[5px] ${hidden ? "hidden" : "flex"}`}
                      >
                        <button
                          className="color-[#979797]"
                          type="button"
                          onClick={() => handleOpenEditModal(field)}
                        >
                          <MdOutlineEdit className="color-[#979797]" />
                        </button>
                        <button
                          className="color-[#979797]"
                          type="button"
                          onClick={() => handleDeleteField(field)}
                        >
                          <MdDeleteOutline className="color-[#979797]" />
                        </button>
                      </div>
                    </div>

                    {/* Render field based on type */}
                    {renderInputField(field)}
                  </div>
                )
            )}
        </form>

        {/* Add Field Button */}
         <div className='grid grid-cols-2 items-center px-10 mt-6'>
          <div className='flex items-center justify-end'>
            <button
              onClick={handleOpenModal}
              className="px-3 py-1 bg-gray-200 font-medium text-gray-600 rounded-md"
            >
              Add Field
            </button>
          </div>
          <div className='flex items-center justify-end'>
            <button
              onClick={handleOpenModal}
              className="px-3 py-1 bg-gray-200 font-medium text-gray-600 rounded-md"
            >
              Add Field
            </button>
          </div>
        </div>
      </div>

      <FieldEditModal
        isOpen={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSave}
        formData={formData}
        setFormData={setFormData}
        content={"File Attachments"}
      />

      {/* Add Custom Field Modal */}
      <CustomFieldModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        division={division}
      />

      {/* Edit Custom Field Modal */}
      <EditCustomFieldModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        onSubmit={handleSubmitUpdateField}
        fieldData={selectedField}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default FilesAttachments;
